import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import {
  Zap,
  ArrowLeft,
  Search,
  Loader2,
  CheckCircle2,
  User,
  Calendar,
  CreditCard,
  DollarSign,
  AlertCircle,
  ChevronRight,
  FileText,
  Check,
  X,
  Plus,
  Trash2,
  TrendingUp,
  RefreshCw,
  Coins,
  Building,
  Package,
  Scissors,
  Layers,
  Tag,
  ShieldCheck,
  Ruler,
  ShoppingCart,
  Info,
  Sparkles
} from 'lucide-react';
import projectsService from '../services/projectsService';
import { clientService } from '../services/api';
import toast from 'react-hot-toast';
import CurrencyInput from './CurrencyInput';
import ConfirmModal from './ConfirmModal';

const PAYMENT_METHODS = [
  { value: 'pix', label: 'PIX', icon: '⚡' },
  { value: 'cash', label: 'Dinheiro em Espécie', icon: '💵' },
  { value: 'credit_card', label: 'Cartão de Crédito', icon: '💳' },
  { value: 'debit_card', label: 'Cartão de Débito', icon: '💳' },
  { value: 'bank_transfer', label: 'Transferência Bancária', icon: '🏦' },
  { value: 'cheque', label: 'Cheque', icon: '📝' },
  { value: 'boleto', label: 'Boleto Bancário', icon: '📄' }
];

const QuickProjectPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'create'

  // Listagem & Filtros
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [searchTermInput, setSearchTermInput] = useState('');
  const [cashboxFilter, setCashboxFilter] = useState('ALL'); // 'ALL' | 'CONFIRMED' | 'PENDING'
  const [filters, setFilters] = useState({
    status: '',
    client_name: '',
    date_from: '',
    date_to: '',
    is_quick: 'true'
  });
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ open: false, projectId: null });

  // Debounce na busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, client_name: searchTermInput }));
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTermInput]);

  // Query de Projetos Rápidos
  const { data: projectsResponse, isLoading: isLoadingProjects, refetch } = useQuery({
    queryKey: ['quick-projects', filters, currentPage],
    queryFn: () => projectsService.getAllProjects({ ...filters, limit: itemsPerPage, page: currentPage }),
    enabled: activeTab === 'list',
    keepPreviousData: true
  });

  const { data: statsResponse } = useQuery({
    queryKey: ['quick-project-stats'],
    queryFn: () => projectsService.getProjectStats({ is_quick: 'true' }),
    enabled: activeTab === 'list'
  });

  const projects = Array.isArray(projectsResponse?.data) ? projectsResponse.data : (projectsResponse?.data?.data || []);
  const pagination = projectsResponse?.pagination || { total: 0, pages: 1 };
  const stats = statsResponse?.data || {};

  // Form State Simplificado
  const getInitialFormData = () => ({
    project_name: '',
    total_amount: '',
    payment_type: 'pix',
    client_id: '',
    client_name: '',
    reference_date: new Date().toISOString().split('T')[0],
    description: '',
    confirm_cashbox_now: true
  });

  const [formData, setFormData] = useState(getInitialFormData());
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Clientes para seleção opcional
  const { data: clientsData, isLoading: loadingClients } = useQuery({
    queryKey: ['clients-quick-select'],
    queryFn: () => clientService.getClients({ limit: 100 }),
    staleTime: 5 * 60 * 1000
  });

  const clientsList = useMemo(() => {
    if (!clientsData) return [];
    if (Array.isArray(clientsData?.data?.data)) return clientsData.data.data;
    if (Array.isArray(clientsData?.data?.clients)) return clientsData.data.clients;
    if (Array.isArray(clientsData?.data)) return clientsData.data;
    return [];
  }, [clientsData]);

  // Itens de Estoque Disponíveis para Venda em Projeto Rápido
  const { data: stockItemsResponse, isLoading: loadingStockItems } = useQuery({
    queryKey: ['quick-stock-items'],
    queryFn: () => projectsService.getQuickStockItems(),
    staleTime: 60 * 1000
  });

  const stockCatalog = useMemo(() => {
    return stockItemsResponse?.data || [];
  }, [stockItemsResponse]);

  const sectorsList = useMemo(() => {
    const set = new Set();
    stockCatalog.forEach(i => {
      if (i.setor_nome) set.add(i.setor_nome);
    });
    return ['TODOS', ...Array.from(set)];
  }, [stockCatalog]);

  const productsGrouped = useMemo(() => {
    const map = {};
    stockCatalog.forEach(item => {
      if (!map[item.produto_id]) {
        map[item.produto_id] = {
          id: item.produto_id,
          nome: item.produto_nome,
          sku: item.produto_sku,
          setor: item.setor_nome,
          unidade: item.unidade_sigla,
          is_sheet: item.is_sheet,
          is_linear: item.is_linear,
          variants: []
        };
      }
      map[item.produto_id].variants.push(item);
    });
    return Object.values(map);
  }, [stockCatalog]);

  // Itens adicionados à venda no projeto rápido
  const [selectedStockItems, setSelectedStockItems] = useState([]);
  const [showItemBuilder, setShowItemBuilder] = useState(false);
  const [builderSector, setBuilderSector] = useState('TODOS');
  const [builderProductId, setBuilderProductId] = useState('');
  const [builderVariantId, setBuilderVariantId] = useState('');
  const [builderMdfMode, setBuilderMdfMode] = useState('HALF_SHEET'); // 'HALF_SHEET' | 'FULL_SHEET' | 'CUSTOM_CUT' | 'SQM'
  const [builderQuantity, setBuilderQuantity] = useState(1);
  const [builderCutLength, setBuilderCutLength] = useState(137.5);
  const [builderCutWidth, setBuilderCutWidth] = useState(183);
  const [builderSqm, setBuilderSqm] = useState(2.5);
  const [builderLinearMeters, setBuilderLinearMeters] = useState(5);
  const [builderUnitPrice, setBuilderUnitPrice] = useState('');

  // Produto e Variante selecionados atualmente no Builder
  const activeProduct = useMemo(() => {
    return productsGrouped.find(p => String(p.id) === String(builderProductId)) || null;
  }, [productsGrouped, builderProductId]);

  const activeVariant = useMemo(() => {
    if (!activeProduct) return null;
    return activeProduct.variants.find(v => String(v.variante_id) === String(builderVariantId)) || activeProduct.variants[0] || null;
  }, [activeProduct, builderVariantId]);

  // Quando o produto ou variante muda, inicializa o preço unitário sugerido
  useEffect(() => {
    if (activeVariant) {
      if (activeProduct?.is_sheet) {
        setBuilderUnitPrice(activeVariant.preco_venda ? String(activeVariant.preco_venda) : '');
      } else {
        setBuilderUnitPrice(activeVariant.preco_venda ? String(activeVariant.preco_venda) : '');
      }
    }
  }, [activeVariant, activeProduct]);

  // Cálculos do item em construção
  const builderCalculations = useMemo(() => {
    if (!activeProduct || !activeVariant) {
      return { subtotal: 0, soldArea: 0, hasScrap: false, scrapArea: 0, scrapComp: 0, scrapLarg: 0, soldQty: 0 };
    }

    const isSheet = activeProduct.is_sheet;
    const isLinear = activeProduct.is_linear;

    if (isSheet) {
      const sheetComp = activeVariant.comprimento_cm || 275.0;
      const sheetLarg = activeVariant.largura_cm || 183.0;
      const sheetArea = activeVariant.sheet_area_m2 || Number(((sheetComp * sheetLarg) / 10000).toFixed(4)) || 5.0325;
      const baseFullPrice = builderUnitPrice !== '' ? (parseFloat(builderUnitPrice) || 0) : (activeVariant.preco_venda || 0);
      const pricePerM2 = sheetArea > 0 ? (baseFullPrice / sheetArea) : 0;

      if (builderMdfMode === 'HALF_SHEET') {
        const soldArea = Number((sheetArea / 2).toFixed(4));
        const subtotal = Number((baseFullPrice / 2).toFixed(2));
        const scrapComp = Number((sheetComp / 2).toFixed(1));
        const scrapLarg = sheetLarg;
        const scrapArea = Number((sheetArea / 2).toFixed(4));
        return {
          soldQty: 0.5,
          unitLabel: 'chapa (1/2)',
          soldArea,
          subtotal,
          hasScrap: true,
          scrapComp,
          scrapLarg,
          scrapArea,
          scrapDesc: `Meia chapa restante (${scrapComp}cm × ${scrapLarg}cm)`,
          chapasConsumidas: 1,
          pricePerM2,
          sheetArea,
          sheetComp,
          sheetLarg,
          baseFullPrice
        };
      }

      if (builderMdfMode === 'FULL_SHEET') {
        const qty = Math.max(1, parseInt(builderQuantity) || 1);
        const soldArea = Number((sheetArea * qty).toFixed(4));
        const subtotal = Number((baseFullPrice * qty).toFixed(2));
        return {
          soldQty: qty,
          unitLabel: qty === 1 ? 'chapa inteira' : 'chapas inteiras',
          soldArea,
          subtotal,
          hasScrap: false,
          scrapComp: 0,
          scrapLarg: 0,
          scrapArea: 0,
          scrapDesc: '',
          chapasConsumidas: qty,
          pricePerM2,
          sheetArea,
          sheetComp,
          sheetLarg,
          baseFullPrice
        };
      }

      if (builderMdfMode === 'CUSTOM_CUT') {
        const cLen = parseFloat(builderCutLength) || 100;
        const cWid = parseFloat(builderCutWidth) || 100;
        const soldArea = Number(((cLen * cWid) / 10000).toFixed(4));
        const subtotal = Number((soldArea * pricePerM2).toFixed(2));
        const scrapComp = Number(Math.max(10, sheetComp - cLen).toFixed(1));
        const scrapLarg = sheetLarg;
        const scrapArea = Number(Math.max(0, sheetArea - soldArea).toFixed(4));
        return {
          soldQty: 1,
          unitLabel: `corte ${cLen}×${cWid}cm (${soldArea} m²)`,
          soldArea,
          subtotal,
          hasScrap: true,
          scrapComp,
          scrapLarg,
          scrapArea,
          scrapDesc: `Sobra útil (${scrapComp}cm × ${scrapLarg}cm)`,
          chapasConsumidas: 1,
          pricePerM2,
          sheetArea,
          sheetComp,
          sheetLarg,
          baseFullPrice
        };
      }

      // 'SQM'
      const sM2 = parseFloat(builderSqm) || 1.0;
      const subtotal = Number((sM2 * pricePerM2).toFixed(2));
      const hasScrap = sM2 < sheetArea;
      const scrapArea = hasScrap ? Number(Math.max(0, sheetArea - sM2).toFixed(4)) : 0;
      const scrapComp = hasScrap && sheetLarg > 0 ? Number(((scrapArea / (sheetLarg / 100)) * 100).toFixed(1)) : 0;
      return {
        soldQty: sM2,
        unitLabel: `${sM2} m²`,
        soldArea: sM2,
        subtotal,
        hasScrap,
        scrapComp,
        scrapLarg: sheetLarg,
        scrapArea,
        scrapDesc: hasScrap ? `Sobra útil (${scrapArea} m²)` : '',
        chapasConsumidas: Math.ceil(sM2 / sheetArea),
        pricePerM2,
        sheetArea,
        sheetComp,
        sheetLarg,
        baseFullPrice
      };
    }

    if (isLinear) {
      const meters = Math.max(0.1, parseFloat(builderLinearMeters) || 1);
      const unitPrice = builderUnitPrice !== '' ? (parseFloat(builderUnitPrice) || 0) : (activeVariant.preco_venda || 0);
      const subtotal = Number((meters * unitPrice).toFixed(2));
      return {
        soldQty: meters,
        unitLabel: `${meters} metros`,
        soldArea: 0,
        subtotal,
        hasScrap: false,
        scrapComp: 0,
        scrapLarg: 0,
        scrapArea: 0,
        chapasConsumidas: 0,
        unitPrice
      };
    }

    // Unidades / Ferragens
    const qty = Math.max(1, parseInt(builderQuantity) || 1);
    const unitPrice = builderUnitPrice !== '' ? (parseFloat(builderUnitPrice) || 0) : (activeVariant.preco_venda || 0);
    const subtotal = Number((qty * unitPrice).toFixed(2));
    return {
      soldQty: qty,
      unitLabel: `${qty} ${activeProduct.unidade || 'un'}`,
      soldArea: 0,
      subtotal,
      hasScrap: false,
      scrapComp: 0,
      scrapLarg: 0,
      scrapArea: 0,
      chapasConsumidas: 0,
      unitPrice
    };
  }, [activeProduct, activeVariant, builderMdfMode, builderQuantity, builderCutLength, builderCutWidth, builderSqm, builderLinearMeters, builderUnitPrice]);

  // Handler para adicionar item à venda
  const handleAddStockItem = () => {
    if (!activeProduct || !activeVariant) {
      toast.error('Selecione um produto e variante válidos');
      return;
    }

    const calc = builderCalculations;
    if (calc.subtotal <= 0) {
      toast.error('O valor do item deve ser maior que zero');
      return;
    }

    const variantLabel = activeVariant.tamanho_label || activeVariant.medida_text || (activeVariant.espessura_mm ? `${activeVariant.espessura_mm}mm` : '') || (activeVariant.cor || '');

    const newItem = {
      id: Date.now(),
      produto_id: activeProduct.id,
      produto_nome: activeProduct.nome,
      produto_sku: activeProduct.sku,
      variante_id: activeVariant.variante_id,
      sku_variacao: activeVariant.sku_variacao,
      cor: activeVariant.cor,
      espessura_mm: activeVariant.espessura_mm,
      tamanho_label: activeVariant.tamanho_label,
      medida_text: activeVariant.medida_text,
      armazem_id: activeVariant.armazem_id || 1,
      armazem_nome: activeVariant.armazem_nome || 'ARM001',
      is_sheet: activeProduct.is_sheet,
      is_linear: activeProduct.is_linear,
      sale_mode: activeProduct.is_sheet ? builderMdfMode : (activeProduct.is_linear ? 'LINEAR' : 'UNIT'),
      quantidade: calc.soldQty,
      unidade: activeProduct.is_sheet ? 'm²' : (activeProduct.is_linear ? 'm' : (activeProduct.unidade || 'UN')),
      unit_label: calc.unitLabel,
      preco_unitario: activeProduct.is_sheet ? calc.pricePerM2 : (calc.unitPrice || calc.baseFullPrice),
      subtotal: calc.subtotal,
      descricao: `${activeProduct.nome} ${variantLabel ? `(${variantLabel})` : ''} - ${calc.unitLabel}`,
      chapas_inteiras_consumidas: calc.chapasConsumidas || 0,
      has_scrap: calc.hasScrap,
      scrap_comprimento_cm: calc.scrapComp,
      scrap_largura_cm: calc.scrapLarg,
      scrap_area_m2: calc.scrapArea,
      scrap_desc: calc.scrapDesc,
      sentido_veio: 'SEM_VEIO'
    };

    setSelectedStockItems(prev => {
      const next = [...prev, newItem];
      const newTotal = next.reduce((sum, it) => sum + (parseFloat(it.subtotal) || 0), 0);
      setFormData(f => ({
        ...f,
        total_amount: newTotal > 0 ? newTotal.toFixed(2).replace('.', ',') : f.total_amount,
        project_name: !f.project_name || f.project_name.startsWith('Venda')
          ? (next.length === 1 ? `Venda de ${newItem.produto_nome} (${newItem.unit_label})` : `Venda Balcão (${next.length} itens do estoque)`)
          : f.project_name
      }));
      return next;
    });

    toast.success(`${activeProduct.nome} adicionado à venda!`);
    setShowItemBuilder(false);
    setBuilderProductId('');
    setBuilderVariantId('');
  };

  const handleRemoveStockItem = (id) => {
    setSelectedStockItems(prev => {
      const next = prev.filter(item => item.id !== id);
      const newTotal = next.reduce((sum, it) => sum + (parseFloat(it.subtotal) || 0), 0);
      setFormData(f => ({
        ...f,
        total_amount: newTotal > 0 ? newTotal.toFixed(2).replace('.', ',') : f.total_amount
      }));
      return next;
    });
  };

  // Soma total de todos os itens de estoque
  const stockItemsTotal = useMemo(() => {
    return selectedStockItems.reduce((sum, it) => sum + (parseFloat(it.subtotal) || 0), 0);
  }, [selectedStockItems]);

  // Mutation para confirmar entrada no caixa
  const confirmCashboxMutation = useMutation({
    mutationFn: (projectId) => projectsService.confirmQuickProjectCashbox(projectId),
    onSuccess: (data) => {
      toast.success(data.message || 'Recebimento confirmado no caixa da empresa!');
      queryClient.invalidateQueries(['quick-projects']);
      queryClient.invalidateQueries(['quick-project-stats']);
      queryClient.invalidateQueries(['company-cashbox-balance']);
      queryClient.invalidateQueries(['cashbox-transactions']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao confirmar no caixa');
    }
  });

  // Mutation para excluir projeto rápido
  const deleteProjectMutation = useMutation({
    mutationFn: (projectId) => projectsService.deleteProject(projectId),
    onSuccess: () => {
      toast.success('Projeto rápido excluído com sucesso');
      queryClient.invalidateQueries(['quick-projects']);
      queryClient.invalidateQueries(['quick-project-stats']);
      queryClient.invalidateQueries(['company-cashbox-balance']);
      setConfirmDeleteModal({ open: false, projectId: null });
    },
    onError: (err) => {
      toast.error('Erro ao excluir projeto: ' + (err.response?.data?.message || err.message));
    }
  });

  // Submeter formulário simplificado
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.project_name?.trim()) {
      toast.error('Por favor, informe o nome do projeto ou serviço');
      return;
    }

    const totalNum = parseFloat(formData.total_amount) || 0;
    if (totalNum <= 0) {
      toast.error('Por favor, informe um valor válido maior que zero');
      return;
    }

    try {
      setLoadingSubmit(true);

      const payload = {
        project_name: formData.project_name.trim(),
        total_amount: totalNum,
        payment_type: formData.payment_type || 'pix',
        client_id: formData.client_id || null,
        client_name: formData.client_name?.trim() || null,
        reference_date: formData.reference_date || new Date().toISOString().split('T')[0],
        description: formData.description?.trim() || `Projeto rápido: ${formData.project_name}`,
        has_entry: false,
        installments: 1,
        stock_items: selectedStockItems.map(item => ({
          produto_id: item.produto_id,
          variante_id: item.variante_id,
          armazem_id: item.armazem_id,
          nome: item.produto_nome,
          produto_nome: item.produto_nome,
          sku_variacao: item.sku_variacao,
          produto_sku: item.produto_sku,
          cor: item.cor,
          espessura_mm: item.espessura_mm,
          quantidade: item.quantidade,
          unidade: item.unidade,
          preco_unitario: item.preco_unitario,
          subtotal: item.subtotal,
          descricao: item.descricao,
          chapas_inteiras_consumidas: item.chapas_inteiras_consumidas,
          has_scrap: item.has_scrap,
          scrap_comprimento_cm: item.scrap_comprimento_cm,
          scrap_largura_cm: item.scrap_largura_cm,
          scrap_area_m2: item.scrap_area_m2,
          scrap_desc: item.scrap_desc,
          sentido_veio: item.sentido_veio
        }))
      };

      const res = await projectsService.createQuickProject(payload);
      const createdProject = res.data?.project;
      const scrapsCount = res.data?.scraps_generated?.length || 0;

      // Se marcado para confirmar no caixa imediatamente
      if (formData.confirm_cashbox_now && createdProject?.id) {
        try {
          await projectsService.confirmQuickProjectCashbox(createdProject.id, {
            payment_method: formData.payment_type
          });
        } catch (cashErr) {
          console.error('Aviso: Projeto criado, mas confirmação de caixa falhou:', cashErr);
        }
      }

      toast.success(
        formData.confirm_cashbox_now
          ? `🚀 Projeto rápido criado e confirmado no caixa da empresa!${scrapsCount > 0 ? ` (${scrapsCount} sobra(s) de MDF cadastrada(s))` : ''}`
          : `Projeto rápido registrado com sucesso!${scrapsCount > 0 ? ` (${scrapsCount} sobra(s) de MDF cadastrada(s))` : ''}`
      );

      queryClient.invalidateQueries(['quick-projects']);
      queryClient.invalidateQueries(['quick-project-stats']);
      queryClient.invalidateQueries(['company-cashbox-balance']);
      queryClient.invalidateQueries(['quick-stock-items']);
      queryClient.invalidateQueries(['inventory-retalhos']);
      queryClient.invalidateQueries(['inventory-products']);

      // Reset e volta para a lista
      setFormData(getInitialFormData());
      setSelectedStockItems([]);
      setShowItemBuilder(false);
      setActiveTab('list');
    } catch (error) {
      console.error('Erro ao criar projeto rápido:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar projeto rápido');
    } finally {
      setLoadingSubmit(false);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
  };

  const getPaymentBadge = (method) => {
    const found = PAYMENT_METHODS.find(m => m.value === method);
    return found ? `${found.icon} ${found.label}` : (method ? String(method).toUpperCase() : 'PIX');
  };

  // Filtragem local por confirmação no caixa se selecionada
  const filteredProjects = useMemo(() => {
    if (cashboxFilter === 'CONFIRMED') {
      return projects.filter(p => p.is_confirmed_in_cashbox || p.status === 'COMPLETED');
    }
    if (cashboxFilter === 'PENDING') {
      return projects.filter(p => !p.is_confirmed_in_cashbox && p.status !== 'COMPLETED');
    }
    return projects;
  }, [projects, cashboxFilter]);

  // Volume financeiro total visível
  const totalVolumeVisible = useMemo(() => {
    return projects.reduce((sum, p) => sum + (parseFloat(p.project_value || p.estimated_budget) || 0), 0);
  }, [projects]);

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Top Header / Breadcrumbs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between py-4 gap-4">
            <div>
              <nav className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <Link to="/dashboard" className="hover:text-gray-700 transition-colors">Início</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-[#49943d] font-semibold">Projetos Rápidos</span>
              </nav>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-[#49943d] shadow-xs">
                  <Zap className="w-5 h-5 fill-[#49943d]" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                    Entradas de Projetos Rápidos
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Registro ágil de projetos, serviços rápidos e confirmação direta no caixa da empresa.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {activeTab === 'list' ? (
                <button
                  type="button"
                  onClick={() => setActiveTab('create')}
                  className="px-5 py-2.5 text-sm text-white bg-[#49943d] hover:bg-[#3a7630] rounded-xl shadow-xs hover:shadow transition-all flex items-center gap-2 font-bold"
                >
                  <Plus className="w-4 h-4" />
                  <span>Novo Projeto Rápido</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setActiveTab('list')}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2 font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Voltar à Lista</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {activeTab === 'list' ? (
          <div className="space-y-6">
            
            {/* KPI Cards Interativos */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              <button
                type="button"
                onClick={() => {
                  setCashboxFilter('ALL');
                  setFilters(prev => ({ ...prev, status: '' }));
                  setCurrentPage(1);
                }}
                className={`p-5 rounded-2xl border transition-all text-left ${
                  cashboxFilter === 'ALL' && filters.status === ''
                    ? 'bg-blue-100/70 border-blue-400 ring-2 ring-blue-400/30 shadow-xs'
                    : 'bg-white border-gray-200 hover:border-gray-300 shadow-xs'
                }`}
              >
                <div className="flex items-center gap-3.5">
                  <div className="p-3 bg-blue-100 text-blue-700 rounded-xl">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Registrado</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.total_projects || projects.length || 0}</p>
                    <span className="text-[11px] text-blue-700 font-semibold">{formatCurrency(totalVolumeVisible)}</span>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setCashboxFilter('CONFIRMED');
                  setCurrentPage(1);
                }}
                className={`p-5 rounded-2xl border transition-all text-left ${
                  cashboxFilter === 'CONFIRMED'
                    ? 'bg-emerald-100/70 border-emerald-400 ring-2 ring-emerald-400/30 shadow-xs'
                    : 'bg-white border-gray-200 hover:border-gray-300 shadow-xs'
                }`}
              >
                <div className="flex items-center gap-3.5">
                  <div className="p-3 bg-emerald-100 text-emerald-700 rounded-xl">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">No Caixa</p>
                    <p className="text-2xl font-bold text-emerald-700">
                      {projects.filter(p => p.is_confirmed_in_cashbox || p.status === 'COMPLETED').length}
                    </p>
                    <span className="text-[11px] text-emerald-600 font-medium">Entradas confirmadas</span>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setCashboxFilter('PENDING');
                  setCurrentPage(1);
                }}
                className={`p-5 rounded-2xl border transition-all text-left ${
                  cashboxFilter === 'PENDING'
                    ? 'bg-amber-100/70 border-amber-400 ring-2 ring-amber-400/30 shadow-xs'
                    : 'bg-white border-gray-200 hover:border-gray-300 shadow-xs'
                }`}
              >
                <div className="flex items-center gap-3.5">
                  <div className="p-3 bg-amber-100 text-amber-700 rounded-xl">
                    <Coins className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">A Confirmar</p>
                    <p className="text-2xl font-bold text-amber-700">
                      {projects.filter(p => !p.is_confirmed_in_cashbox && p.status !== 'COMPLETED').length}
                    </p>
                    <span className="text-[11px] text-amber-600 font-medium">Aguardando confirmação</span>
                  </div>
                </div>
              </button>

              <div className="p-5 rounded-2xl border border-gray-200 bg-white shadow-xs">
                <div className="flex items-center gap-3.5">
                  <div className="p-3 bg-purple-100 text-purple-700 rounded-xl">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Volume em Caixa</p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                      {formatCurrency(
                        projects
                          .filter(p => p.is_confirmed_in_cashbox || p.status === 'COMPLETED')
                          .reduce((sum, p) => sum + (parseFloat(p.project_value || p.estimated_budget) || 0), 0)
                      )}
                    </p>
                    <span className="text-[11px] text-purple-700 font-medium">Lançado no financeiro</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Barra de Filtros */}
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="lg:col-span-2">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Buscar Projeto ou Cliente
                  </label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Nome do projeto, serviço ou cliente..."
                      value={searchTermInput}
                      onChange={(e) => setSearchTermInput(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#49943d]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Status no Caixa
                  </label>
                  <select
                    value={cashboxFilter}
                    onChange={(e) => {
                      setCashboxFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#49943d]"
                  >
                    <option value="ALL">Todos os status</option>
                    <option value="CONFIRMED">Confirmados no Caixa</option>
                    <option value="PENDING">Pendentes de Confirmação</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTermInput('');
                      setCashboxFilter('ALL');
                      setFilters({ status: '', client_name: '', date_from: '', date_to: '', is_quick: 'true' });
                      setCurrentPage(1);
                    }}
                    className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-200 flex items-center justify-center gap-2 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Limpar Filtros</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Tabela de Projetos Rápidos */}
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-gray-50/50">
                <h2 className="text-sm font-bold text-gray-900">Entradas de Projetos Rápidos</h2>
                <span className="text-xs font-semibold text-gray-500">
                  Mostrando {filteredProjects.length} de {pagination.total || filteredProjects.length} registro(s)
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Código</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Projeto / Serviço</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Cliente</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Valor</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Forma Pgto</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Status no Caixa</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Data</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoadingProjects ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-[#49943d]" />
                          <span>Carregando projetos rápidos...</span>
                        </td>
                      </tr>
                    ) : filteredProjects.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                          <Zap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-base font-semibold text-gray-700 mb-1">Nenhum projeto rápido encontrado</p>
                          <p className="text-xs text-gray-400 mb-4">Registre novas entradas de serviços rápidos de balcão sem burocracia.</p>
                          <button
                            onClick={() => setActiveTab('create')}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#49943d] hover:bg-[#3a7630] text-white rounded-xl text-xs font-bold shadow-xs transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            <span>Registrar Primeiro Projeto Rápido</span>
                          </button>
                        </td>
                      </tr>
                    ) : (
                      filteredProjects.map((p) => {
                        const isConfirmed = p.is_confirmed_in_cashbox || p.status === 'COMPLETED';
                        const val = parseFloat(p.project_value || p.estimated_budget) || 0;

                        return (
                          <tr key={p.id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap font-mono font-bold text-xs text-gray-900">
                              {p.project_code || `PROJ-${p.id}`}
                            </td>

                            <td className="px-4 py-3">
                              <div className="font-bold text-gray-900">{p.project_name}</div>
                              {p.project_description && p.project_description !== p.project_name && (
                                <div className="text-[11px] text-gray-500 line-clamp-1">{p.project_description}</div>
                              )}
                            </td>

                            <td className="px-4 py-3">
                              {p.client_full_name || (p.client_name && p.client_name !== 'Cliente Avulso') ? (
                                <div className="font-medium text-gray-900">{p.client_full_name || p.client_name}</div>
                              ) : (
                                <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-600">
                                  Cliente Avulso
                                </span>
                              )}
                            </td>

                            <td className="px-4 py-3 whitespace-nowrap text-right font-mono font-bold text-gray-900 text-sm">
                              {formatCurrency(val)}
                            </td>

                            <td className="px-4 py-3 whitespace-nowrap text-center text-xs">
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-medium bg-slate-100 text-slate-800 border border-slate-200">
                                {getPaymentBadge(p.quick_payment_type || p.payment_type)}
                              </span>
                            </td>

                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              {isConfirmed ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                  <Check className="w-3.5 h-3.5" /> Confirmado no Caixa
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                  <AlertCircle className="w-3.5 h-3.5" /> Pendente no Caixa
                                </span>
                              )}
                            </td>

                            <td className="px-4 py-3 whitespace-nowrap text-center text-xs text-gray-500">
                              {new Date(p.created_at).toLocaleDateString('pt-BR')}
                            </td>

                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                {!isConfirmed && (
                                  <button
                                    type="button"
                                    onClick={() => confirmCashboxMutation.mutate(p.id)}
                                    disabled={confirmCashboxMutation.isPending}
                                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-2xs transition-colors"
                                    title="Confirmar lançamento da receita no caixa da empresa"
                                  >
                                    <Coins className="w-3.5 h-3.5" />
                                    <span>Confirmar no Caixa</span>
                                  </button>
                                )}

                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteModal({ open: true, projectId: p.id })}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Excluir projeto rápido"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {pagination.pages > 1 && (
                <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs disabled:opacity-50 hover:bg-white font-medium text-gray-700 transition-colors"
                  >
                    Anterior
                  </button>
                  <span className="text-xs font-medium text-gray-600">
                    Página {currentPage} de {pagination.pages} ({pagination.total || 0} projetos)
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                    disabled={currentPage === pagination.pages}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs disabled:opacity-50 hover:bg-white font-medium text-gray-700 transition-colors"
                  >
                    Próxima
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* FORMULÁRIO SIMPLIFICADO: REGISTRO DE ENTRADAS DE PROJETOS RÁPIDOS */
          <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-800 to-green-700 text-white p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Registrar Projeto Rápido</h2>
                  <p className="text-xs text-green-100">Entrada simples de valor e forma de pagamento com baixa direta no caixa</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('list')}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* 1. Nome do Projeto / Serviço */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Nome do Projeto ou Serviço *
                </label>
                <input
                  type="text"
                  placeholder="Ex: Balcão de Recepção, Ajuste de Armário, Gaveteiro Sob Medida..."
                  value={formData.project_name}
                  onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#49943d] font-semibold text-gray-900"
                  required
                />
              </div>

              {/* 1.1 MÓDULO: VENDER ITENS DO ESTOQUE (MDF com Sobras, Ferragens, PVC e Insumos) */}
              <div className="p-4 bg-emerald-50/60 rounded-2xl border border-emerald-200/80 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <Package className="w-4 h-4 text-emerald-700" />
                      <span>Vender Itens do Estoque (Opcional)</span>
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Venda chapas de MDF (com geração automática de sobras), ferragens por unidade ou fitas PVC.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowItemBuilder(!showItemBuilder)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-semibold shadow-xs transition-colors self-start"
                  >
                    {showItemBuilder ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    <span>{showItemBuilder ? 'Fechar Seletor' : 'Adicionar Item do Estoque'}</span>
                  </button>
                </div>

                {/* Construtor Interativo de Item */}
                {showItemBuilder && (
                  <div className="bg-white rounded-xl p-4 border border-emerald-300 shadow-sm space-y-4 animate-in fade-in duration-150">
                    {/* Filtro por Setor */}
                    <div>
                      <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                        1. Filtrar por Categoria / Setor
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {sectorsList.map(sec => (
                          <button
                            key={sec}
                            type="button"
                            onClick={() => { setBuilderSector(sec); setBuilderProductId(''); setBuilderVariantId(''); }}
                            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                              builderSector === sec
                                ? 'bg-emerald-700 text-white shadow-xs'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            {sec}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Seleção do Produto e da Variante */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-gray-700 mb-1">
                          2. Selecionar Produto *
                        </label>
                        <select
                          value={builderProductId}
                          onChange={(e) => {
                            setBuilderProductId(e.target.value);
                            setBuilderVariantId('');
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-900 font-medium focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="">Selecione um produto...</option>
                          {productsGrouped
                            .filter(p => builderSector === 'TODOS' || p.setor === builderSector)
                            .map(p => (
                              <option key={p.id} value={p.id}>
                                {p.nome} {p.sku ? `(${p.sku})` : ''} - {p.variants.length} var.
                              </option>
                            ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-gray-700 mb-1">
                          3. Selecionar Variante / Especificação *
                        </label>
                        <select
                          disabled={!activeProduct}
                          value={builderVariantId}
                          onChange={(e) => setBuilderVariantId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-900 font-medium focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                        >
                          <option value="">Selecione a variante...</option>
                          {activeProduct?.variants.map(v => {
                            const spec = [
                              v.tamanho_label,
                              v.cor,
                              v.espessura_mm ? `${v.espessura_mm}mm` : null,
                              v.medida_text
                            ].filter(Boolean).join(' • ');
                            return (
                              <option key={v.variante_id} value={v.variante_id}>
                                {spec || v.sku_variacao || 'Padrão'} | Disp: {v.estoque_disponivel} | R$ {Number(v.preco_venda || 0).toFixed(2)}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    {/* Parametrização Dinâmica do Item Selecionado */}
                    {activeProduct && activeVariant && (
                      <div className="pt-3 border-t border-gray-100 space-y-3">
                        {/* SE FOR MDF / CHAPA (Venda no Metro Quadrado ou Meia Chapa com Sobra) */}
                        {activeProduct.is_sheet ? (
                          <div className="bg-emerald-50/60 rounded-xl p-3 border border-emerald-200 space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                              <span className="font-bold text-emerald-900 flex items-center gap-1.5">
                                <Layers className="w-4 h-4 text-emerald-700" />
                                <span>Chapa de MDF: {activeVariant.comprimento_cm || 275}cm × {activeVariant.largura_cm || 183}cm ({activeVariant.sheet_area_m2 || 5.03} m²)</span>
                              </span>
                              <span className="text-gray-600 bg-white px-2 py-0.5 rounded border border-emerald-200">
                                Espessura: <strong>{activeVariant.espessura_mm || 15}mm</strong>
                              </span>
                            </div>

                            {/* Seletor de Modo de Venda do MDF */}
                            <div>
                              <label className="block text-[11px] font-bold text-gray-700 mb-1.5 uppercase">
                                Modo de Venda do MDF:
                              </label>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-semibold">
                                <button
                                  type="button"
                                  onClick={() => setBuilderMdfMode('HALF_SHEET')}
                                  className={`p-2 rounded-lg border text-center transition-all ${
                                    builderMdfMode === 'HALF_SHEET'
                                      ? 'bg-emerald-700 text-white border-emerald-700 shadow-xs'
                                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <div>Meia Chapa (1/2)</div>
                                  <div className="text-[10px] opacity-80 mt-0.5">Gera sobra útil</div>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => setBuilderMdfMode('FULL_SHEET')}
                                  className={`p-2 rounded-lg border text-center transition-all ${
                                    builderMdfMode === 'FULL_SHEET'
                                      ? 'bg-emerald-700 text-white border-emerald-700 shadow-xs'
                                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <div>Chapa Inteira</div>
                                  <div className="text-[10px] opacity-80 mt-0.5">Sem sobra</div>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => setBuilderMdfMode('CUSTOM_CUT')}
                                  className={`p-2 rounded-lg border text-center transition-all ${
                                    builderMdfMode === 'CUSTOM_CUT'
                                      ? 'bg-emerald-700 text-white border-emerald-700 shadow-xs'
                                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <div>Corte Sob Medida</div>
                                  <div className="text-[10px] opacity-80 mt-0.5">Comp. × Larg.</div>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => setBuilderMdfMode('SQM')}
                                  className={`p-2 rounded-lg border text-center transition-all ${
                                    builderMdfMode === 'SQM'
                                      ? 'bg-emerald-700 text-white border-emerald-700 shadow-xs'
                                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <div>Metro² Avulso</div>
                                  <div className="text-[10px] opacity-80 mt-0.5">Área em m²</div>
                                </button>
                              </div>
                            </div>

                            {/* Campos condicionais de dimensões de venda do MDF */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {builderMdfMode === 'FULL_SHEET' && (
                                <div>
                                  <label className="block text-[11px] font-bold text-gray-700 mb-1">Qtd. Chapas Inteiras</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={builderQuantity}
                                    onChange={(e) => setBuilderQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 bg-white"
                                  />
                                </div>
                              )}

                              {builderMdfMode === 'CUSTOM_CUT' && (
                                <>
                                  <div>
                                    <label className="block text-[11px] font-bold text-gray-700 mb-1">Comprimento Corte (cm)</label>
                                    <input
                                      type="number"
                                      step="0.5"
                                      value={builderCutLength}
                                      onChange={(e) => setBuilderCutLength(parseFloat(e.target.value) || 0)}
                                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 bg-white"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[11px] font-bold text-gray-700 mb-1">Largura Corte (cm)</label>
                                    <input
                                      type="number"
                                      step="0.5"
                                      value={builderCutWidth}
                                      onChange={(e) => setBuilderCutWidth(parseFloat(e.target.value) || 0)}
                                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 bg-white"
                                    />
                                  </div>
                                </>
                              )}

                              {builderMdfMode === 'SQM' && (
                                <div>
                                  <label className="block text-[11px] font-bold text-gray-700 mb-1">Área Vendida (m²)</label>
                                  <input
                                    type="number"
                                    step="0.1"
                                    value={builderSqm}
                                    onChange={(e) => setBuilderSqm(parseFloat(e.target.value) || 0)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 bg-white"
                                  />
                                </div>
                              )}

                              <div>
                                <label className="block text-[11px] font-bold text-gray-700 mb-1">Preço da Chapa Inteira (R$)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={builderUnitPrice}
                                  onChange={(e) => setBuilderUnitPrice(e.target.value)}
                                  placeholder="0,00"
                                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 bg-white font-bold"
                                />
                              </div>

                              <div className="flex flex-col justify-end">
                                <span className="text-[11px] text-gray-500">Valor do m²:</span>
                                <span className="text-xs font-bold text-gray-800">R$ {builderCalculations.pricePerM2.toFixed(2)}/m²</span>
                              </div>
                            </div>

                            {/* Alerta Visual de Geração de Sobra */}
                            {builderCalculations.hasScrap && (
                              <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-900 flex items-start gap-2">
                                <Scissors className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <strong>🪵 Sobra Automática de MDF:</strong> Será deduzida 1 chapa inteira e cadastrada automaticamente uma sobra de <strong>{builderCalculations.scrapComp}cm × {builderCalculations.scrapLarg}cm ({builderCalculations.scrapArea} m²)</strong> no módulo de Sobras/Retalhos MDF para reaproveitamento!
                                </div>
                              </div>
                            )}
                          </div>
                        ) : activeProduct.is_linear ? (
                          /* SE FOR PVC / FITA DE BORDA (Venda por Metro Linear) */
                          <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                            <div>
                              <label className="block text-[11px] font-bold text-gray-700 mb-1">Metragem Vendida (Metros)</label>
                              <input
                                type="number"
                                step="0.5"
                                min="0.5"
                                value={builderLinearMeters}
                                onChange={(e) => setBuilderLinearMeters(parseFloat(e.target.value) || 1)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 bg-white"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] font-bold text-gray-700 mb-1">Preço por Metro (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={builderUnitPrice}
                                onChange={(e) => setBuilderUnitPrice(e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 bg-white font-bold"
                              />
                            </div>
                          </div>
                        ) : (
                          /* SE FOR FERRAGEM / UNIDADE (Corrediças, Dobradiças, Puxadores, Pistões...) */
                          <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                            <div>
                              <label className="block text-[11px] font-bold text-gray-700 mb-1">
                                Quantidade ({activeProduct.unidade || 'unidades/pares'})
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={builderQuantity}
                                onChange={(e) => setBuilderQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 bg-white"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] font-bold text-gray-700 mb-1">Preço Unitário (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={builderUnitPrice}
                                onChange={(e) => setBuilderUnitPrice(e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 bg-white font-bold"
                              />
                            </div>
                          </div>
                        )}

                        {/* Subtotal do Item e Botão de Adicionar */}
                        <div className="flex items-center justify-between pt-2">
                          <div>
                            <span className="text-xs text-gray-500">Subtotal deste item:</span>
                            <span className="text-base font-extrabold text-emerald-800 ml-2">
                              {formatCurrency(builderCalculations.subtotal)}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={handleAddStockItem}
                            className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5"
                          >
                            <Plus className="w-4 h-4" />
                            <span>Incluir na Venda</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Lista de Itens Já Adicionados ao Projeto Rápido */}
                {selectedStockItems.length > 0 ? (
                  <div className="bg-white rounded-xl border border-emerald-200 overflow-hidden shadow-2xs">
                    <div className="px-3 py-2 bg-emerald-100/60 border-b border-emerald-200 flex justify-between items-center text-xs">
                      <span className="font-bold text-emerald-950 flex items-center gap-1.5">
                        <ShoppingCart className="w-3.5 h-3.5 text-emerald-700" />
                        <span>Itens Selecionados ({selectedStockItems.length})</span>
                      </span>
                      <span className="font-extrabold text-emerald-900">
                        Total Itens: {formatCurrency(stockItemsTotal)}
                      </span>
                    </div>

                    <div className="divide-y divide-gray-100">
                      {selectedStockItems.map((item) => (
                        <div key={item.id} className="p-3 flex items-start justify-between gap-3 text-xs hover:bg-gray-50">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-gray-900">{item.produto_nome}</span>
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-700">
                                {item.unit_label}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5">{item.descricao}</p>
                            {item.has_scrap && (
                              <div className="mt-1 text-[11px] font-semibold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 w-fit flex items-center gap-1">
                                <Scissors className="w-3 h-3 text-amber-600" />
                                <span>Gera sobra no estoque: {item.scrap_comprimento_cm}cm × {item.scrap_largura_cm}cm ({item.scrap_area_m2} m²)</span>
                              </div>
                            )}
                          </div>
                          <div className="text-right flex items-center gap-3 flex-shrink-0">
                            <div>
                              <span className="text-xs font-bold text-gray-900 block">{formatCurrency(item.subtotal)}</span>
                              <span className="text-[10px] text-gray-400">({formatCurrency(item.preco_unitario)}/{item.unidade})</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveStockItem(item.id)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remover item da venda"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-2 text-xs text-gray-400 italic">
                    Nenhum item do estoque adicionado ainda. Se for apenas serviço ou mão de obra avulsa, preencha o valor diretamente abaixo.
                  </div>
                )}
              </div>

              {/* 2. Valor e Forma de Pagamento */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Valor Total (R$) *
                  </label>
                  <CurrencyInput
                    value={formData.total_amount}
                    onChange={(val) => setFormData({ ...formData, total_amount: val })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-base font-mono font-bold text-gray-900 focus:ring-2 focus:ring-[#49943d]"
                    placeholder="0,00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Maneira Como Foi Pago *
                  </label>
                  <select
                    value={formData.payment_type}
                    onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm bg-white font-semibold text-gray-800 focus:ring-2 focus:ring-[#49943d]"
                    required
                  >
                    {PAYMENT_METHODS.map(m => (
                      <option key={m.value} value={m.value}>{m.icon} {m.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 3. Cliente (Opcional) */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Vincular a um Cliente (Opcional)
                  </label>
                  <span className="text-[11px] text-gray-400 font-medium">Deixe em branco se for cliente avulso</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-1">Selecionar da Lista</label>
                    <select
                      value={formData.client_id}
                      onChange={(e) => {
                        const cId = e.target.value;
                        const found = clientsList.find(c => String(c.id) === String(cId));
                        setFormData({
                          ...formData,
                          client_id: cId,
                          client_name: found ? (found.full_name || found.name) : formData.client_name
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-800"
                    >
                      <option value="">Nenhum / Cliente Avulso...</option>
                      {clientsList.map(c => (
                        <option key={c.id} value={c.id}>{c.full_name || c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] text-gray-500 mb-1">Ou Digite o Nome do Cliente</label>
                    <input
                      type="text"
                      placeholder="Nome do cliente avulso..."
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value, client_id: '' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs text-gray-800"
                    />
                  </div>
                </div>
              </div>

              {/* 4. Data e Observações */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Data do Recebimento / Referência
                  </label>
                  <input
                    type="date"
                    value={formData.reference_date}
                    onChange={(e) => setFormData({ ...formData, reference_date: e.target.value })}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Observações (Opcional)
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: pago à vista no balcão da marcenaria..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
              </div>

              {/* 5. Checkbox de confirmação automática no caixa */}
              <div className="p-3.5 bg-emerald-50 rounded-xl border border-emerald-200">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.confirm_cashbox_now}
                    onChange={(e) => setFormData({ ...formData, confirm_cashbox_now: e.target.checked })}
                    className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-xs font-bold text-emerald-900">
                    Confirmar e lançar entrada no Caixa da Empresa imediatamente ao salvar
                  </span>
                </label>
              </div>

              {/* Botões do Formulário */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setActiveTab('list')}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-semibold transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loadingSubmit}
                  className="px-6 py-2.5 bg-[#49943d] hover:bg-[#3a7630] text-white rounded-xl text-sm font-bold shadow-md flex items-center gap-2 transition-all disabled:opacity-50"
                >
                  {loadingSubmit ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Salvar Projeto Rápido</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Modal de Exclusão */}
      {confirmDeleteModal.open && (
        <ConfirmModal
          isOpen={confirmDeleteModal.open}
          onClose={() => setConfirmDeleteModal({ open: false, projectId: null })}
          onConfirm={() => deleteProjectMutation.mutate(confirmDeleteModal.projectId)}
          title="Excluir Projeto Rápido"
          message="Tem certeza que deseja excluir este projeto rápido? Se houver lançamento no caixa, ele será estornado."
          confirmText="Sim, Excluir"
          cancelText="Cancelar"
          type="danger"
        />
      )}
    </div>
  );
};

export default QuickProjectPage;
